/**
 * CancelInviteModal
 *
 * Confirmation modal for cancelling a pending invite.
 *
 * ## Component Hierarchy
 * - **Level**: Organism
 * - **Feature**: Users
 * - **Location**: `src/features/users/components/ui/organisms/CancelInviteModal/`
 *
 * ## Features
 * - Shows confirmation message with invite email
 * - Calls cancel API on confirmation
 * - Removes cancelled invite from query cache
 * - Shows error/success messages
 *
 * @module features/users/components/ui/organisms/CancelInviteModal
 */
import React, { useCallback } from 'react';
import { App, Modal, Typography } from 'antd';
import { Button } from '@/shared/components/ui/atoms';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { InviteItem } from '@/features/users/types/users.types';
import { cancelUserInvite } from '@/features/users/services/userInvites';
import { USER_INVITES_QUERY_KEY } from '@/features/users/hooks/useUserInvites';

import './CancelInviteModal.css';

export interface CancelInviteModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** The invite to cancel */
  invite: InviteItem | null;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when cancellation is successful */
  onSuccess?: () => void;
}

/**
 * CancelInviteModal Component
 *
 * @param props - Component properties
 * @returns React component
 *
 * @example
 * ```tsx
 * <CancelInviteModal
 *   open={isOpen}
 *   invite={selectedInvite}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => refetchInvites()}
 * />
 * ```
 */
export const CancelInviteModal: React.FC<CancelInviteModalProps> = ({
  open,
  invite,
  onClose,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const { mutateAsync: doCancel, isPending } = useMutation({
    mutationFn: (inviteId: string) => cancelUserInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_INVITES_QUERY_KEY });
    },
  });

  const handleConfirm = useCallback(async () => {
    if (!invite) return;

    try {
      await doCancel(invite.id);
      notification.success({
        message: t('Invite cancelled'),
        placement: 'topRight',
      });
      onSuccess?.();
      onClose();
    } catch {
      notification.error({
        message: t('Failed to cancel invite'),
        placement: 'topRight',
      });
    }
  }, [invite, doCancel, notification, t, onSuccess, onClose]);

  const inviteEmail = invite?.email ?? '';

  return (
    <Modal
      open={open}
      title={t('Cancel invite')}
      onCancel={onClose}
      width={370}
      footer={
        <div className="cancel-invite-modal__footer">
          <Button variant="ghost" size="lg" fullWidth={false} onClick={onClose}>
            {t('Close')}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            fullWidth={false}
            loading={isPending}
            onClick={handleConfirm}
          >
            {t('Cancel invite')}
          </Button>
        </div>
      }
      destroyOnHidden
      className="cancel-invite-modal"
      aria-label={t('Cancel invite confirmation modal')}
    >
      <div className="cancel-invite-modal__content">
        <Typography.Text className="cancel-invite-modal__question">
          {t('Are you sure you want to cancel the invitation sent to the')}
          <strong> {inviteEmail}</strong>
        </Typography.Text>
      </div>
    </Modal>
  );
};
